<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';

const props = defineProps({
  offer: { type: Object, default: null },
  isLoading: { type: Boolean, default: false },
  error: { type: String, default: '' },
});

const emit = defineEmits(['revoke', 'reissue', 'clearError']);

const { t } = useI18n();

const action = ref('');
const reason = ref('');
const confirmation = ref('');
const submittedAction = ref('');

const publicLink = computed(() => props.offer?.public_link || {});
const status = computed(() => publicLink.value.status || 'unknown');
const statusKey = computed(() => String(status.value).toUpperCase());
const statusTone = computed(() => {
  const tones = {
    active: 'success',
    expired: 'warning',
    revoked: 'danger',
    substituted: 'info',
  };
  return tones[status.value] || 'neutral';
});
const previousStatus = computed(() => publicLink.value.previous_token_status);
const canRevoke = computed(() => status.value === 'active');
const showModal = computed(() => Boolean(action.value));
const actionConfig = computed(() => {
  if (action.value === 'revoke') {
    return {
      title: t('PROPERTY_OFFERS.PUBLIC_LINK.REVOKE_TITLE'),
      description: t('PROPERTY_OFFERS.PUBLIC_LINK.REVOKE_DESCRIPTION'),
      confirmation: 'REVOGAR',
      confirm: t('PROPERTY_OFFERS.PUBLIC_LINK.REVOKE'),
      icon: 'i-lucide-ban',
    };
  }

  return {
    title: t('PROPERTY_OFFERS.PUBLIC_LINK.REISSUE_TITLE'),
    description: t('PROPERTY_OFFERS.PUBLIC_LINK.REISSUE_DESCRIPTION'),
    confirmation: 'REEMITIR',
    confirm: t('PROPERTY_OFFERS.PUBLIC_LINK.REISSUE'),
    icon: 'i-lucide-refresh-cw',
  };
});
const reasonValid = computed(() => reason.value.trim().length > 0);
const confirmationValid = computed(
  () =>
    confirmation.value.trim().toUpperCase() === actionConfig.value.confirmation
);
const canSubmit = computed(
  () => reasonValid.value && confirmationValid.value && !props.isLoading
);

watch(
  () => props.offer?.id,
  () => {
    action.value = '';
    reason.value = '';
    confirmation.value = '';
  }
);

const openAction = nextAction => {
  emit('clearError');
  action.value = nextAction;
  reason.value = '';
  confirmation.value = '';
  submittedAction.value = '';
};

const close = () => {
  if (props.isLoading) return;
  action.value = '';
};

const submit = () => {
  if (!canSubmit.value) return;
  submittedAction.value = action.value;
  const payload = {
    reason: reason.value.trim(),
    confirmation: confirmation.value.trim().toUpperCase(),
  };
  if (action.value === 'revoke') emit('revoke', payload);
  else emit('reissue', payload);
};

watch(
  () => [props.isLoading, props.error],
  ([loading, error]) => {
    if (loading || error || !submittedAction.value) return;

    action.value = '';
    reason.value = '';
    confirmation.value = '';
    submittedAction.value = '';
  }
);

const formatDate = value => {
  if (!value) return t('PROPERTY_OFFERS.PUBLIC_LINK.NOT_AVAILABLE');
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return value;
  }
};
</script>

<template>
  <section
    class="min-w-0 rounded-lais border border-lais-border bg-white p-4"
    :aria-label="t('PROPERTY_OFFERS.PUBLIC_LINK.TITLE')"
  >
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="min-w-0">
        <h3
          class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
        >
          {{ t('PROPERTY_OFFERS.PUBLIC_LINK.TITLE') }}
        </h3>
        <p class="mt-1 text-lais-body-sm text-lais-text-muted">
          {{ t('PROPERTY_OFFERS.PUBLIC_LINK.DESCRIPTION') }}
        </p>
      </div>
      <LaisStatusBadge
        :label="t(`PROPERTY_OFFERS.PUBLIC_LINK.STATUS.${statusKey}`)"
        :tone="statusTone"
      />
    </div>

    <dl class="mt-4 grid gap-2 text-lais-body-sm">
      <div class="flex justify-between gap-3">
        <dt class="text-lais-text-muted">
          {{ t('PROPERTY_OFFERS.PUBLIC_LINK.REFERENCE') }}
        </dt>
        <dd class="text-right font-semibold text-lais-text">
          {{
            publicLink.token_last_four
              ? t('PROPERTY_OFFERS.PUBLIC_LINK.LAST_FOUR', {
                  value: publicLink.token_last_four,
                })
              : t('PROPERTY_OFFERS.PUBLIC_LINK.REDACTED')
          }}
        </dd>
      </div>
      <div class="flex justify-between gap-3">
        <dt class="text-lais-text-muted">
          {{ t('PROPERTY_OFFERS.PUBLIC_LINK.EXPIRES_AT') }}
        </dt>
        <dd class="text-right font-semibold text-lais-text">
          {{ formatDate(publicLink.expires_at) }}
        </dd>
      </div>
      <div
        v-if="previousStatus === 'substituted'"
        class="rounded-lais-sm bg-lais-primary-50 px-3 py-2 text-lais-body-sm text-lais-primary"
      >
        {{ t('PROPERTY_OFFERS.PUBLIC_LINK.SUBSTITUTED_NOTICE') }}
      </div>
    </dl>

    <div class="mt-4 flex flex-wrap gap-2">
      <LaisPrimaryButton
        v-if="canRevoke"
        size="sm"
        variant="ghost"
        icon="i-lucide-ban"
        :disabled="isLoading"
        @click="openAction('revoke')"
      >
        {{ t('PROPERTY_OFFERS.PUBLIC_LINK.REVOKE') }}
      </LaisPrimaryButton>
      <LaisPrimaryButton
        size="sm"
        icon="i-lucide-refresh-cw"
        :disabled="isLoading"
        @click="openAction('reissue')"
      >
        {{ t('PROPERTY_OFFERS.PUBLIC_LINK.REISSUE') }}
      </LaisPrimaryButton>
    </div>

    <LaisModal :show="showModal" :title="actionConfig.title" @close="close">
      <div class="grid gap-4">
        <p class="text-lais-body-sm text-lais-text-body">
          {{ actionConfig.description }}
        </p>
        <p
          v-if="error"
          class="rounded-lais-sm bg-lais-danger-50 px-3 py-2 text-lais-body-sm text-lais-danger"
          role="alert"
        >
          {{ error }}
        </p>
        <label
          class="grid gap-1 text-lais-label-sm font-semibold text-lais-text"
        >
          {{ t('PROPERTY_OFFERS.PUBLIC_LINK.REASON') }}
          <textarea
            v-model="reason"
            rows="3"
            class="min-h-[88px] rounded-lais border border-lais-border px-3 py-2 text-lais-body-sm font-normal text-lais-text focus:border-lais-primary focus:outline-none"
          />
        </label>
        <label
          class="grid gap-1 text-lais-label-sm font-semibold text-lais-text"
        >
          {{
            t('PROPERTY_OFFERS.PUBLIC_LINK.CONFIRMATION', {
              value: actionConfig.confirmation,
            })
          }}
          <input
            v-model="confirmation"
            type="text"
            class="h-10 rounded-lais border border-lais-border px-3 text-lais-body-sm font-normal text-lais-text focus:border-lais-primary focus:outline-none"
            autocomplete="off"
          />
        </label>
      </div>
      <template #footer>
        <div class="flex flex-wrap justify-end gap-2">
          <LaisPrimaryButton
            variant="ghost"
            :disabled="isLoading"
            @click="close"
          >
            {{ t('PROPERTY_OFFERS.PUBLIC_LINK.CANCEL') }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            :icon="actionConfig.icon"
            :is-loading="isLoading"
            :disabled="!canSubmit"
            @click="submit"
          >
            {{ actionConfig.confirm }}
          </LaisPrimaryButton>
        </div>
      </template>
    </LaisModal>
  </section>
</template>
